/*
  New Grange — verlanglijst.

  Bewaart een lijst meubels in localStorage. Bewust geen app en geen
  klantaccount: accounts staan uit op deze winkel, en een inlogdrempel past
  niet bij een publiek dat juist persoonlijk contact zoekt.

  Wat er wordt bewaard is met opzet minimaal: per regel de variant-id en de
  producthandle. De handle is nodig om later /products/<handle>.js te kunnen
  ophalen; de variant-id om binnen dat product de juiste uitvoering te vinden.
  Prijzen en titels bewaren we NIET — die zouden verouderen zodra Diane iets
  aanpast. Ze worden bij het tonen vers opgehaald.

  De lijst is apparaatgebonden. Dat is een bewuste afweging: het doel is dat
  iemand binnen een bezoek meerdere meubels verzamelt en daar één offerte voor
  aanvraagt, niet dat hij de lijst maanden bewaart.
*/
(function () {
  'use strict';

  var KEY = 'ng_wishlist_v1';
  var listeners = [];

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Alleen regels met beide velden; een half opgeslagen regel is nutteloos.
      return parsed.filter(function (item) {
        return item && item.id && item.handle;
      });
    } catch (e) {
      // Privémodus of vol geheugen: dan is er simpelweg geen lijst.
      return [];
    }
  }

  function write(items) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* stil: opslaan kan geweigerd worden, de UI blijft dan gewoon werken */
    }
    notify(items);
  }

  function notify(items) {
    listeners.forEach(function (fn) {
      try {
        fn(items);
      } catch (e) {}
    });
    document.dispatchEvent(new CustomEvent('ng:wishlist:change', { detail: { items: items } }));
  }

  function has(id) {
    return read().some(function (item) {
      return String(item.id) === String(id);
    });
  }

  function add(id, handle, meta) {
    if (!id || !handle) return read();
    var items = read();
    if (has(id)) return items;
    items.push({ id: String(id), handle: handle, added: Date.now() });
    write(items);
    document.dispatchEvent(
      new CustomEvent('ng:wishlist:add', { detail: { id: String(id), handle: handle, meta: meta || {} } })
    );
    return items;
  }

  function remove(id) {
    var items = read().filter(function (item) {
      return String(item.id) !== String(id);
    });
    write(items);
    document.dispatchEvent(new CustomEvent('ng:wishlist:remove', { detail: { id: String(id) } }));
    return items;
  }

  function toggle(id, handle, meta) {
    return has(id) ? (remove(id), false) : (add(id, handle, meta), true);
  }

  /* ---------- knoppen ---------- */

  function paintButton(btn) {
    var id = btn.getAttribute('data-ng-wishlist-id');
    var on = has(id);
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var label = on ? btn.getAttribute('data-label-remove') : btn.getAttribute('data-label-add');
    if (label) {
      btn.setAttribute('aria-label', label);
      var text = btn.querySelector('[data-ng-wishlist-text]');
      if (text) text.textContent = label;
    }
  }

  function paintAllButtons() {
    document.querySelectorAll('[data-ng-wishlist-id]').forEach(paintButton);
  }

  /*
    De badge draagt de klasse cart-count-bubble en leunt daarmee op de bestaande
    regel `.cart-count-bubble:empty { display: none }`. Bij nul zetten we de
    inhoud dus leeg in plaats van het element te verbergen: een hidden-attribuut
    verliest het van `display: flex` uit die klasse, en dan blijft er een zwarte
    bol met een 0 staan.
  */
  function paintCount(items) {
    var list = items || read();
    document.querySelectorAll('[data-ng-wishlist-bubble]').forEach(function (el) {
      el.textContent = list.length > 0 ? String(list.length) : '';
    });
  }

  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-ng-wishlist-id]');
    if (!btn) return;
    event.preventDefault();
    toggle(btn.getAttribute('data-ng-wishlist-id'), btn.getAttribute('data-ng-wishlist-handle'), {
      title: btn.getAttribute('data-ng-wishlist-title') || ''
    });
    paintButton(btn);
  });

  /* ---------- productgegevens ophalen ---------- */

  var cache = {};

  function fetchProduct(handle) {
    if (cache[handle]) return cache[handle];
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    cache[handle] = fetch(root + 'products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('niet gevonden');
        return r.json();
      })
      .catch(function () {
        return null;
      });
    return cache[handle];
  }

  /*
    Haalt de volledige lijst op als bruikbare regels. Producten die niet meer
    bestaan of varianten die zijn verwijderd, vallen er stilzwijgend uit en
    worden meteen uit de opslag gehaald — anders blijft een bezoeker naar een
    dode regel kijken.
  */
  function resolve() {
    var items = read();
    if (!items.length) return Promise.resolve([]);

    return Promise.all(
      items.map(function (item) {
        return fetchProduct(item.handle).then(function (product) {
          if (!product) return null;
          var variant = (product.variants || []).filter(function (v) {
            return String(v.id) === String(item.id);
          })[0];
          if (!variant) return null;
          return {
            id: String(item.id),
            handle: item.handle,
            // De product-id wordt niet getoond, maar is wel nodig om een meubel
            // in de statistieken hetzelfde te noemen als bij "toevoegen aan mijn
            // collectie" — dat event stuurt de product-id mee. Zonder dit veld
            // zou hetzelfde meubel in GA4 onder twee identiteiten belanden.
            productId: product.id,
            title: product.title,
            variantTitle: variant.public_title || '',
            price: variant.price,
            available: variant.available,
            url: product.url + '?variant=' + variant.id,
            image: variant.featured_image ? variant.featured_image.src : product.featured_image
          };
        });
      })
    ).then(function (rows) {
      var clean = rows.filter(Boolean);
      if (clean.length !== items.length) {
        write(
          clean.map(function (row) {
            return { id: row.id, handle: row.handle };
          })
        );
      }
      return clean;
    });
  }

  window.NGWishlist = {
    read: read,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    count: function () {
      return read().length;
    },
    resolve: resolve,
    subscribe: function (fn) {
      listeners.push(fn);
      return fn;
    },
    repaint: function () {
      paintAllButtons();
      paintCount();
    }
  };

  /*
    Op de productpagina hoort het hartje bij de gekozen uitvoering, niet bij het
    product. Kiest iemand een andere maat, dan moet het hartje die maat bewaren
    — en moet het leeg staan als díe maat nog niet bewaard is.

    De variantkeuze van Craft schrijft de nieuwe id in het verborgen veld
    .product-variant-id. Daar haken we op aan in plaats van de variantlogica te
    dupliceren.
  */
  function syncVariantHearts() {
    document.querySelectorAll('[data-ng-wishlist-follow]').forEach(function (btn) {
      var scope = btn.closest('.ng-lead__actions') || btn.closest('.product') || document;
      var field = scope.querySelector('.product-variant-id');
      if (!field || !field.value) return;
      if (btn.getAttribute('data-ng-wishlist-id') === field.value) return;
      btn.setAttribute('data-ng-wishlist-id', field.value);
      paintButton(btn);
    });
  }

  /*
    Geen filter op de aanleiding. De maatselector van deze productpagina staat
    in .ng-dimensions en heeft geen name-attribuut, dus filteren op
    variant-selects of [name^="options"] miste hem volledig. Elke change op de
    pagina is goedkoop genoeg om gewoon te controleren of het verborgen
    variantveld inmiddels iets anders zegt.

    Twee keer kijken: de variantwissel gaat via een fetch die de sectie
    opnieuw rendert, dus het veld is niet meteen bij.
  */
  document.addEventListener('change', function () {
    setTimeout(syncVariantHearts, 60);
    setTimeout(syncVariantHearts, 500);
  });

  function init() {
    paintAllButtons();
    paintCount();
    syncVariantHearts();
  }

  document.addEventListener('ng:wishlist:change', function (e) {
    paintAllButtons();
    paintCount(e.detail.items);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Terugkeren via de browserknop levert een pagina uit de cache; dan kloppen
  // hartjes en teller niet meer met de opslag.
  window.addEventListener('pageshow', init);
})();
